<template>
  <div
    v-show="hideOnLoading ? !loading : true"
    ref="wrapper"
    :class="[size, { show: open }]"
    class="select-picker dropdown"
  >
    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-skeleton type="button" :width="(minWidth / 1.5).toString() + 'px'" />
      </template>
      <b-btn
        :size="size"
        :variant="btnVariant"
        class="dropdown-toggle"
        :class="btnClass"
        @click="openBox"
      >
        <template v-if="selected.value">
          {{ selected.value }}
        </template>
        <template v-else>
          {{ placeholder }}
        </template>
      </b-btn>
    </b-skeleton-wrapper>
    <transition name="fadeUpShort">
      <div
        v-if="open"
        :style="{
          'max-height': `${maxHeight}px`,
          'min-width': `${minWidth}px`,
        }"
        class="picker-container"
        :class="pickerClass"
      >
        <div class="search">
          <b-input-group size="sm" class="input-group-labeled">
            <b-form-input
              ref="searchInput"
              v-model="query"
              placeholder="Vyhľadať..."
              @keydown.enter="search"
            />
            <b-input-group-append>
              <b-btn class="px-2" variant="link" @click="search">
                <b-icon icon="search" />
              </b-btn>
              <b-btn v-if="query" class="px-2" variant="link" @click="reset">
                <b-icon icon="x-circle" />
              </b-btn>
            </b-input-group-append>
          </b-input-group>
        </div>
        <div ref="scroll" v-bar>
          <div infinite-wrapper style="min-height: 45px">
            <b-list-group class="list-group-anim">
              <b-list-group-item
                v-for="item in options"
                :key="item[trackBy]"
                :active="selected.id === item[trackBy]"
                href="#"
                class="flex-column align-items-start border-left-0 border-right-0"
                :class="{ inactive: !item.active }"
                @click="setSelected(item)"
              >
                <slot :option="item" name="option" />
              </b-list-group-item>
            </b-list-group>
            <infinite-loading
              ref="infiniteLoading"
              force-use-infinite-wrapper="true"
              spinner="spiral"
              @infinite="infiniteHandler"
            >
              <template #spinner>
                <b-overlay :blur="null" class="my-4" opacity="0" show />
              </template>
              <div slot="no-results">
                <div class="text-center my-2">
                  <h4>Žiadne dáta</h4>
                </div>
              </div>
              <div slot="no-more" />
            </infinite-loading>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import InfiniteLoading from 'vue-infinite-loading'

export default {
  components: {
    InfiniteLoading,
  },
  props: {
    value: {
      type: [String, Number],
      default: null,
    },
    fetchUrl: {
      type: String,
      required: true,
    },
    params: {
      type: Object,
    },
    label: {
      type: [String, Array],
      default: 'name',
    },
    trackBy: {
      type: String,
      default: 'id',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    hideOnLoading: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '-',
    },
    btnVariant: {
      type: String,
      default: 'link',
    },
    minWidth: {
      type: Number,
      default: 270,
    },
    maxHeight: {
      type: Number,
      default: 300,
    },
    btnClass: {
      type: [String, Array, Object],
    },
    pickerClass: {
      type: [String, Array, Object],
    },
    closeOnClick: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'md',
    },
  },
  data() {
    return {
      loading: false,
      options: [],
      selected: {
        id: null,
        value: '',
      },
      selectedItem: {},
      query: '',
      page: 1,
      open: this.visible,
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.searchInput.$el.focus()
        })
        this.reset()
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.documentClick)
  },
  created() {
    if (this.value) {
      this.loading = true
      this.$api
        .get(this.fetchUrl, { params: { id: this.value } })
        .then((res) => {
          this.setSelected(res.data.data, false)
          this.loading = false
          this.$emit('loaded', this.selectedItem)
        })
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.documentClick)
  },
  methods: {
    documentClick(e) {
      let el = this.$refs.wrapper
      let target = e.target
      if (el !== target && !el.contains(target) && !this.visible) {
        this.closeBox()
      }
    },
    setSelected(item, emit = true) {
      let label = ''
      if (Array.isArray(this.label)) {
        label =
          item[this.label[0]] ||
          item[this.label[1]] ||
          item[this.label[2]] ||
          item[this.label[3]]
      } else {
        label = item[this.label]
      }
      this.selected = {
        id: item[this.trackBy],
        value: label,
      }
      this.selectedItem = item
      if (emit) {
        this.$emit('input', this.selected.id)
        this.$emit('selectedItem', this.selectedItem)
      }
      if (this.closeOnClick) {
        this.closeBox()
      }
    },
    infiniteHandler($state) {
      this.fetchList($state)
    },
    fetchList($state) {
      return this.$api
        .get(this.fetchUrl, {
          params: {
            search: this.query,
            page: this.page,
            ...this.params,
          },
        })
        .then((res) => {
          if (res.data.meta.count) {
            this.page++
            this.options = this.options.concat(res.data.data)
            if ($state) {
              $state.loaded()
            }
          } else {
            $state.complete()
          }
          this.$vuebar.refreshScrollbar(this.$refs.scroll, {})
        })
    },
    search() {
      this.options = []
      this.page = 1
      this.$nextTick(() => {
        this.$vuebar.refreshScrollbar(this.$refs.scroll, {})
        this.$refs.infiniteLoading.$emit('$InfiniteLoading:reset')
      })
    },
    reset() {
      this.query = ''
      this.search()
    },
    openBox() {
      this.open = true
    },
    closeBox() {
      this.open = false
    },
  },
}
</script>
